import { StrictMode, Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { I18nextProvider } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'

import * as Sentry from '@sentry/react'
import { isAxiosError } from 'axios'
import ReactDOM from 'react-dom/client'
import { SWRConfig } from 'swr'

import { App } from './App'
import { Braze, ReproBooster, ScrollToTop } from './components'
import { initBraze } from './components/contents/Braze/brazeClient'
import { PATH } from './constants'
import i18n from './i18n'
import './styles/app.scss'

import { version } from '../package.json'

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://c368f834f9844107bf16c2614a006822@o44447.ingest.sentry.io/4504677435637760',
    normalizeDepth: 10,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 10 }),
    ],
    tracesSampleRate: 1.0,
    // Only report errors thrown from our own bundle. Third-party scripts
    // (Repro Booster, GTM tags) and scripts that browsers / in-app browsers
    // inject inline into the page cannot be fixed on our side.
    allowUrls: [`${window.location.origin}/assets/`],
    ignoreErrors: [
      // Our API calls go through axios (XHR), so a bare fetch failure comes
      // from third-party tags blocked by ad blockers or a flaky network.
      // Anchored so that "Failed to fetch dynamically imported module" is still reported.
      /^Failed to fetch$/,
      // A bug inside @sentry/core 7.x itself while dropping an event:
      // SentryError's constructor assigns this.message, which throws in some
      // environments. Still present in 7.120.
      'An event processor returned `null`',
    ],
  })
  initBraze()
}

const release = `<!-- v${version} -->`

ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <HelmetProvider>
          <SWRConfig
            value={{
              onError: (error) => {
                if (
                  isAxiosError(error) &&
                  error.response?.status === 500 &&
                  window.location.pathname !== PATH.errors.serverError
                ) {
                  window.location.href = PATH.errors.serverError
                }
              },
            }}
          >
            <ReproBooster />
            <Suspense fallback={<div>Loading</div>}>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: release }} />
              <ScrollToTop />
              <App />
              <Braze />
            </Suspense>
          </SWRConfig>
        </HelmetProvider>
      </BrowserRouter>
    </I18nextProvider>
  </StrictMode>
)
